import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import Component from '@glimmer/component';
import BasicDropdown from 'ember-basic-dropdown/components/basic-dropdown';
import svgJar from 'ember-svg-jar/helpers/svg-jar';
import downloadDocument from 'garaje/utils/download-document';

import { type ProfileDocument, getStatusDisplay, formatUploadDate } from './document-status';

interface DocumentListRowSignature {
  Args: {
    document: ProfileDocument;
    onOpen?: (document: ProfileDocument) => void;
  };
  Element: HTMLDivElement;
}

export default class DocumentListRow extends Component<DocumentListRowSignature> {
  get statusConfig(): { label: string; bgClass: string; textClass: string } | null {
    return getStatusDisplay(this.args.document.approvalStatus);
  }

  get subtitle(): string | undefined {
    return formatUploadDate(this.args.document.createdAt);
  }

  @action
  openAndClose(close: () => void): void {
    close();
    this.args.onOpen?.(this.args.document);
  }

  @action
  downloadAndClose(close: () => void): void {
    close();
    const { downloadUrl, title } = this.args.document;
    if (!downloadUrl) return;
    void downloadDocument(downloadUrl, title);
  }

  <template>
    <div
      class="bg-white flex items-center gap-4 p-3"
      style="box-shadow: inset 0 -1px 0 0 #EEEFF5"
      data-test-document-list-row={{@document.id}}
      ...attributes
    >
      {{! Document icon }}
      <div
        class="bg-carbon-5 border border-carbon-10 rounded-[5px] shrink-0 w-9 h-11 flex flex-col items-start pl-1.5 pr-1 pt-2 pb-3 gap-0.5"
      >
        <div class="bg-carbon-50 h-[3px] rounded-sm w-[17px]"></div>
        <div class="bg-carbon-20 h-[3px] rounded-sm w-full"></div>
        <div class="bg-carbon-20 h-[3px] rounded-sm w-full"></div>
        <div class="bg-carbon-20 h-[3px] rounded-sm w-3"></div>
        <div class="bg-carbon-20 h-[3px] rounded-sm w-full"></div>
      </div>

      {{! Title and description }}
      <div class="flex flex-col justify-center flex-1 min-w-0 overflow-hidden">
        <p class="text-sm font-semibold text-carbon-80 leading-normal m-0 truncate" data-test-list-row-title>
          {{@document.title}}
        </p>
        {{#if this.subtitle}}
          <p class="text-xs text-carbon-40 leading-[1.25] m-0 truncate" data-test-list-row-description>
            {{this.subtitle}}
          </p>
        {{/if}}
      </div>

      {{! Status pill }}
      {{#if this.statusConfig}}
        <span
          class="inline-flex items-center shrink-0 px-2 py-1 rounded-lg text-xs font-bold uppercase tracking-[1px] leading-none
            {{this.statusConfig.bgClass}}
            {{this.statusConfig.textClass}}"
          data-test-list-row-status
        >
          {{this.statusConfig.label}}
        </span>
      {{/if}}

      {{! Three-dot menu }}
      <div class="shrink-0">
        <BasicDropdown @horizontalPosition="left" as |dropdown|>
          <dropdown.Trigger>
            <button
              type="button"
              class="flex items-center justify-center w-[28px] h-[28px] bg-carbon-5 rounded-lg text-carbon-40 hover:text-carbon-60 hover:bg-carbon-10 transition-colors"
              aria-label="Document actions"
              data-test-list-row-menu-trigger
            >
              {{svgJar "horizontal-dots" width="18" height="4" class="block shrink-0"}}
            </button>
          </dropdown.Trigger>
          <dropdown.Content
            style="background: transparent; border: none; box-shadow: none; border-radius: 16px; overflow: hidden;"
          >
            <div class="bg-white rounded-2xl shadow-md p-1 min-w-[180px]">
              <button
                type="button"
                class="flex items-center gap-2 w-full px-2 py-2 text-base text-carbon-70 rounded-lg hover:bg-carbon-5 transition-colors"
                data-test-list-row-open
                {{on "click" (fn this.openAndClose dropdown.actions.close)}}
              >
                {{svgJar "expand-corners" width="14" height="14" class="shrink-0"}}
                <span>Open</span>
              </button>
              <button
                type="button"
                class="flex items-center gap-2 w-full px-2 py-2 text-base text-carbon-70 rounded-lg hover:bg-carbon-5 transition-colors"
                data-test-list-row-download
                {{on "click" (fn this.downloadAndClose dropdown.actions.close)}}
              >
                {{svgJar "download" width="16" height="16" class="shrink-0"}}
                <span>Download</span>
              </button>
            </div>
          </dropdown.Content>
        </BasicDropdown>
      </div>
    </div>
  </template>
}
